import React from 'react';
import { Calendar, MapPin, Users, Clock, ExternalLink } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { useSystemPreset } from '../../../../contexts/SystemPresetContext';
import type { CommunityEvent } from '../../../../types/communityEvents';

interface EventCardProps {
  event: CommunityEvent;
  onClick: () => void;
  onRSVP?: (eventId: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick, onRSVP }) => {
  const { language, isRTL } = useLocalization();
  const { presetData } = useSystemPreset();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get event type from cached presets
  const getEventType = () => {
    if (!presetData?.eventTypes) return null;
    return presetData.eventTypes.find(type => type.id === event.communityEventTypeId);
  };

  const eventType = getEventType();

  const getEventStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'completed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const isEventUpcoming = new Date(event.startDate) > new Date();
  const isEventToday = new Date(event.startDate).toDateString() === new Date().toDateString();

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-slate-200 dark:border-slate-700 group overflow-hidden">
      {/* Event Image */}
      {event.imageURL && (
        <div className="h-48 bg-slate-200 dark:bg-slate-700 relative overflow-hidden">
          <img 
            src={event.imageURL} 
            alt={event.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
          <div className="absolute top-4 right-4">
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${getEventStatusColor(event.status)}`}>
              {language === 'ar' ? 
                (event.status === 'published' ? 'منشور' : 
                 event.status === 'cancelled' ? 'ملغي' : 
                 event.status === 'completed' ? 'مكتمل' : 'مسودة') :
                event.status.charAt(0).toUpperCase() + event.status.slice(1)
              }
            </span>
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className={`flex items-start justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white group-hover:text-primary transition-colors mb-2">
              {event.title}
            </h3>
            <p className="text-slate-600 dark:text-slate-300 text-sm line-clamp-2">
              {event.description}
            </p>
          </div>
        </div>

        {/* Event Type */}
        {eventType && (
          <div className="mb-3">
            <span 
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            >
              {language === 'ar' ? eventType.name_ar : eventType.name_en}
            </span>
          </div>
        )}

        {/* Event Details */}
        <div className="space-y-3 mb-4">
          {/* Date & Time */}
          <div className={`flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Calendar className="w-4 h-4" />
            <span>
              {formatDate(event.startDate)} {formatTime(event.startDate)}
              {event.endDate !== event.startDate && ` - ${formatTime(event.endDate)}`}
            </span>
          </div>

          {/* Location */}
          <div className={`flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {event.isOnline ? (
              <>
                <ExternalLink className="w-4 h-4" />
                <span>
                  {language === 'ar' ? 'فعالية أونلاين' : 'Online Event'}
                  {event.meetingLink && (
                    <a 
                      href={event.meetingLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline ml-1"
                    >
                      {language === 'ar' ? '(رابط)' : '(Link)'}
                    </a>
                  )}
                </span>
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4" />
                <span>
                  {event.venueName || event.address}
                  {event.city && `, ${event.city}`}
                </span>
              </>
            )}
          </div>

          {/* Attendees */}
          <div className={`flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Users className="w-4 h-4" />
            <span>
              {event.currentAttendees}
              {event.maxAttendees && ` / ${event.maxAttendees}`}
              {language === 'ar' ? ' مشارك' : ' attendees'}
            </span>
          </div>

          {/* Organizer */}
          <div className={`flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Clock className="w-4 h-4" />
            <span>
              {language === 'ar' ? 'منظم:' : 'Organized by:'} {event.organizerName}
            </span>
          </div>
        </div>

        {/* Price */}
        {!event.isFree && event.price && (
          <div className="mb-4">
            <span className="text-lg font-semibold text-primary">
              {event.price} {event.currency}
            </span>
          </div>
        )}

        {/* Tags */}
        {event.tags.length > 0 && (
          <div className={`flex flex-wrap gap-2 mb-4 ${isRTL ? 'justify-end' : 'justify-start'}`}>
            {event.tags.slice(0, 3).map((tag, index) => (
              <span 
                key={index}
                className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md"
              >
                #{tag}
              </span>
            ))}
            {event.tags.length > 3 && (
              <span className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md">
                +{event.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            onClick={onClick}
            className="flex-1 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            {language === 'ar' ? 'عرض التفاصيل' : 'View Details'}
          </button>
          {onRSVP && isEventUpcoming && event.isRegistrationRequired && (
            <button
              onClick={() => onRSVP(event.id)}
              className="px-4 py-2 border border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-white transition-colors"
            >
              {language === 'ar' ? 'تسجيل' : 'RSVP'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
